import { Injectable } from '@angular/core';
import { BaseDataStore } from './base-data.store';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { ICatchHubLogFilter, ICatchHubLogModel, CatchHubLogModel } from '../../models/catchHubLog/catchHubLog.model';
import { List } from 'immutable';
import { HttpResponse } from '@angular/common/http';
import { CatchHubLogsService } from '../catchHubLog.service';

@Injectable({
  providedIn: 'root',
})
export class CatchHubLogStore extends BaseDataStore {
  private _logs: BehaviorSubject<List<ICatchHubLogModel>> = new BehaviorSubject(List<ICatchHubLogModel>());
  private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public $logs: Observable<List<ICatchHubLogModel>> = this._logs.asObservable();
  public $isLoading: Observable<boolean> = this._isLoading.asObservable();

  constructor(private logService: CatchHubLogsService) {
    super();
  }

  public getTotalLogsCount(orgId?: string, filter?: ICatchHubLogFilter): Observable<number> {
    return this.logService.getCatchHubLogCount(orgId, filter);
  }

  public deleteOrgLogs(orgId?: string): Observable<boolean> {
    return this.logService.deleteOrgLogs(orgId);
  }

  public getEventTypes(orgId?: string): Observable<string[]> {
    // Defend the string[] contract: a non-array shape from the endpoint would otherwise flow
    // straight into the component's eventTypeOptions and the p-select.
    return this.logService.getEventTypes(orgId).pipe(map((types) => (Array.isArray(types) ? types : [])));
  }

  public exportLogs(orgId?: string, filter?: ICatchHubLogFilter): Observable<HttpResponse<Blob>> {
    return this.logService.exportCatchHubLogs(orgId, filter);
  }

  public loadData(pageNumber: Number, pageSize: Number, orgId?: string, filter?: ICatchHubLogFilter): void {
    this._isLoading.next(true);
    this.logService.getCatchHubLogs(pageSize, pageNumber, orgId, filter).subscribe(
      (res: any) => {
        if (res) {
          const logs = (res as ICatchHubLogModel[]).map(
            (log: ICatchHubLogModel) =>
              new CatchHubLogModel(
                log.id,
                log.description,
                log.createdDate,
                log.modifiedDate,
                log.createdBy,
                log.modifiedBy,
                log.nextGenProjectName,
                log.nextGenProjectId,
                log.eventType,
                log.nodeKey,
                log.externalItemId,
                log.logType,
                log.userName,
              ),
          );
          this._logs.next(List(logs));
        }
        this._isLoading.next(false);
      },
      (err) => {
        console.log('Error retrieving catchhub logs');
        this._isLoading.next(false);
      },
    );
  }

  public addLogs(logs: ICatchHubLogModel[]): void {
    this.logService.addCatchHubLogs(logs).subscribe();
  }

  /** Writes rows without an error toast; a failed write is only logged to the console. */
  public addLogsQuietly(logs: ICatchHubLogModel[]): void {
    this.logService.addCatchHubLogsQuietly(logs).subscribe({
      error: (err) => console.error('CatchHub log write failed.', err),
    });
  }
}
